import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, RotateCcw, Flag } from "lucide-react";
import { useT } from "@/i18n";
import SpeakButton, { playClip } from "@/components/SpeakButton";

const TRACK = 100;
const SPEED = 6.5;
const TX = {
  es: { title: "Carrera de autos: Lumi y Spark", sub: "Elige tu piloto. Mueve tu auto arriba y abajo (botones o flechas ↑ ↓) para recoger zanahorias y esquivar las piedras. ¡Gana quien llegue a la meta con más zanahorias!", pick: "Elige tu piloto:", start: "¡En sus marcas, listos, fuera!", up: "Arriba", down: "Abajo", again: "Otra carrera", you: "Tú", cpu: "Compu", win: "¡Yupiii! ¡Ganaste con más zanahorias!", lose: "¡Uy! Esta vez ganó", tryAgain: "¡Inténtalo otra vez!", tie: "¡Empate! Los dos tienen las mismas zanahorias.", carrots: "zanahorias" },
  en: { title: "Car race: Lumi & Spark", sub: "Pick your driver. Move your car up and down (buttons or ↑ ↓ arrows) to collect carrots and dodge the rocks. Whoever reaches the finish with the most carrots wins!", pick: "Pick your driver:", start: "Ready, set, go!", up: "Up", down: "Down", again: "Race again", you: "You", cpu: "CPU", win: "Yippee! You won with the most carrots!", lose: "Oops! This time the winner is", tryAgain: "Try again!", tie: "It's a tie! You both have the same carrots.", carrots: "carrots" },
};
const NAME = { lumi: "Lumi", spark: "Spark" };
const CAR = { lumi: "bg-yellow-400", spark: "bg-blue-600" };

const makeItems = () => {
  const items = [];
  for (let p = 10; p < 94; p += 6 + Math.random() * 3) {
    const type = Math.random() < 0.72 ? "carrot" : "rock";
    items.push({ pos: p, row: Math.random() < 0.5 ? 0 : 1, type, taken: false, cpuHits: type === "carrot" ? Math.random() < 0.75 : Math.random() < 0.05 });
  }
  return items;
};

const Carrot = () => (
  <span className="relative inline-block w-5 h-7">
    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-2 bg-green-500 rounded-full" />
    <span className="absolute top-1.5 left-0 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[22px] border-l-transparent border-r-transparent border-t-orange-500" />
  </span>
);
const Rock = () => <span className="inline-block w-7 h-5 rounded-[50%_50%_40%_40%] bg-stone-500 border-2 border-stone-700" />;

const Car = ({ who, label }) => (
  <div className="flex flex-col items-center">
    <span className="text-[10px] font-bold bg-white/90 rounded-full px-2 text-blue-950 -mb-1 z-10">{label}</span>
    <img src={`/img/${who}.webp`} alt={NAME[who]} className="w-9 sm:w-10 -mb-3 z-10 drop-shadow" draggable="false" />
    <div className={`relative w-16 sm:w-20 h-6 rounded-t-2xl rounded-b-md ${CAR[who]} border-2 border-white shadow-md`}>
      <span className="absolute -bottom-2 left-2 w-4 h-4 rounded-full bg-slate-900 border-2 border-slate-400" />
      <span className="absolute -bottom-2 right-2 w-4 h-4 rounded-full bg-slate-900 border-2 border-slate-400" />
    </div>
  </div>
);

const Lane = ({ who, label, pos, row, items, score, carrotsLabel, testId }) => (
  <div className="relative h-36 sm:h-40 bg-slate-600 border-y-4 border-dashed border-white/70 my-2" data-testid={testId}>
    <div className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-yellow-300/70" />
    <div className="absolute right-[10%] top-0 bottom-0 w-3 bg-[repeating-linear-gradient(0deg,#fff_0_8px,#111_8px_16px)]" />
    <span className="absolute top-1 left-2 bg-orange-500 text-white rounded-full px-3 py-0.5 text-sm font-bold z-20" data-testid={`${testId}-score`}>{score} {carrotsLabel}</span>
    {items.filter((it) => !it.taken).map((it, i) => (
      <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `calc(${it.pos * 0.9}% - ${it.pos * 0.05}rem + 2.25rem)`, top: it.row === 0 ? "30%" : "75%" }}>{it.type === "carrot" ? <Carrot /> : <Rock />}</span>
    ))}
    <div className="absolute -translate-y-1/2 transition-[top] duration-150" style={{ left: `calc(${pos * 0.9}% - ${pos * 0.05}rem)`, top: row === 0 ? "26%" : "70%" }}>
      <Car who={who} label={label} />
    </div>
  </div>
);

export default function RaceGame() {
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const [hero, setHero] = useState("lumi");
  const [state, setState] = useState("idle");
  const [view, setView] = useState(null);
  const [result, setResult] = useState(null);
  const g = useRef(null);
  const rival = hero === "lumi" ? "spark" : "lumi";

  const move = useCallback((row) => { if (g.current && state === "playing") g.current.row = row; }, [state]);

  const start = () => {
    g.current = { pos: 0, row: 1, score: 0, items: makeItems(), cpu: { pos: 0, row: 0, score: 0, items: makeItems() }, slowUntil: 0, last: performance.now() };
    setResult(null);
    setState("playing");
  };

  useEffect(() => {
    if (state !== "playing") return undefined;
    let raf;
    const pass = (racer, prev, decide) => {
      racer.items.forEach((it) => {
        if (it.taken || !(prev < it.pos && racer.pos >= it.pos)) return;
        if (decide(it)) {
          it.taken = true;
          if (it.type === "carrot") racer.score += 1;
          else racer.score = Math.max(0, racer.score - 1);
          return it.type === "rock";
        }
        return false;
      });
    };
    const tick = (now) => {
      const s = g.current;
      const dt = Math.min(0.05, (now - s.last) / 1000);
      s.last = now;
      const prev = s.pos;
      s.pos = Math.min(TRACK, s.pos + (s.slowUntil > now ? SPEED * 0.4 : SPEED) * dt);
      s.items.forEach((it) => {
        if (it.taken || it.row !== s.row || !(prev < it.pos && s.pos >= it.pos)) return;
        it.taken = true;
        if (it.type === "carrot") s.score += 1;
        else { s.score = Math.max(0, s.score - 1); s.slowUntil = now + 800; }
      });
      const c = s.cpu;
      const cPrev = c.pos;
      c.pos = Math.min(TRACK, c.pos + (c.slowUntil > now ? SPEED * 0.4 : SPEED) * dt);
      const next = c.items.find((it) => !it.taken && it.pos > cPrev && it.pos - cPrev < 5);
      if (next) c.row = next.cpuHits ? next.row : 1 - next.row;
      const cScore = c.score;
      pass(c, cPrev, (it) => it.row === c.row);
      if (c.score < cScore) c.slowUntil = now + 800;
      setView({ pos: s.pos, row: s.row, score: s.score, items: s.items, cpu: { pos: c.pos, row: c.row, score: c.score, items: c.items } });
      if (s.pos >= TRACK && c.pos >= TRACK) {
        const w = s.score > c.score ? hero : s.score < c.score ? rival : null;
        setResult({ winner: w, me: s.score, cpu: c.score });
        setState("over");
        if (w) playClip(`${w}-yupi`, lang);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state, hero, rival, lang]);

  useEffect(() => {
    const onKey = (e) => {
      if (state !== "playing") return;
      if (e.code === "ArrowUp") { e.preventDefault(); move(0); }
      if (e.code === "ArrowDown") { e.preventDefault(); move(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, move]);

  const v = view || { pos: 0, row: 1, score: 0, items: [], cpu: { pos: 0, row: 0, score: 0, items: [] } };
  return (
    <div className="space-y-4" data-testid="race-game">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.title}</h3>
        <SpeakButton compact mascot="spark" text={`${tx.title}. ${tx.sub}`} testId="ask-games-race" />
      </div>
      <p className="font-semibold text-blue-900">{tx.sub}</p>
      {state === "idle" && (
        <div className="flex flex-wrap items-center gap-2" data-testid="race-pick">
          <span className="font-bold text-blue-950">{tx.pick}</span>
          {["lumi", "spark"].map((m) => (
            <button key={m} type="button" onClick={() => setHero(m)} className={`rounded-full pl-1 pr-4 py-1 font-fredoka font-bold inline-flex items-center gap-2 shadow border-4 ${hero === m ? "bg-yellow-300 border-yellow-400 text-blue-950" : "bg-white border-white text-blue-900"}`} data-testid={`race-pick-${m}`}>
              <img src={`/img/${m}.webp`} alt="" className="w-9 h-9 object-contain" /> {NAME[m]}
            </button>
          ))}
        </div>
      )}
      <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-lg bg-green-500 select-none py-1" data-testid="race-track">
        <Lane who={hero} label={tx.you} pos={v.pos} row={v.row} items={v.items} score={v.score} carrotsLabel={tx.carrots} testId="race-lane-hero" />
        <Lane who={rival} label={tx.cpu} pos={v.cpu.pos} row={v.cpu.row} items={v.cpu.items} score={v.cpu.score} carrotsLabel={tx.carrots} testId="race-lane-cpu" />
        <Flag className="absolute top-1 right-[9%] w-6 h-6 text-white" />
        {state === "over" && result && (
          <div className="absolute inset-0 bg-blue-950/60 flex flex-col items-center justify-center text-center p-4 z-30" data-testid="race-result">
            {result.winner && <img src={`/img/${result.winner}.webp`} alt="" className="w-20 animate-bounce" />}
            <p className="font-fredoka text-2xl sm:text-3xl font-bold text-yellow-300" data-testid="race-result-text">
              {!result.winner ? tx.tie : result.winner === hero ? tx.win : `${tx.lose} ${NAME[result.winner]}. ${tx.tryAgain}`}
            </p>
            <p className="mt-1 font-bold text-white" data-testid="race-result-score">{tx.you}: {result.me} · {tx.cpu}: {result.cpu} {tx.carrots}</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        {state !== "playing" ? (
          <button type="button" onClick={start} className="glossy-btn bg-yellow-300 text-blue-950 font-fredoka font-bold text-lg px-6 py-2.5 rounded-full inline-flex items-center gap-2" data-testid="race-start-btn">
            {state === "over" ? <RotateCcw className="w-5 h-5" /> : <Flag className="w-5 h-5" />} {state === "over" ? tx.again : tx.start}
          </button>
        ) : (
          <>
            <button type="button" onPointerDown={() => move(0)} className="glossy-btn bg-sky-500 text-white font-fredoka font-bold text-2xl px-8 py-4 rounded-full inline-flex items-center gap-2 active:scale-95" data-testid="race-up-btn"><ArrowUp className="w-7 h-7" /> {tx.up}</button>
            <button type="button" onPointerDown={() => move(1)} className="glossy-btn bg-orange-500 text-white font-fredoka font-bold text-2xl px-8 py-4 rounded-full inline-flex items-center gap-2 active:scale-95" data-testid="race-down-btn"><ArrowDown className="w-7 h-7" /> {tx.down}</button>
          </>
        )}
      </div>
    </div>
  );
}
